/**
 * Money presentation vocabulary, shared by `MoneyController` and by the views
 * that ask it for a tone. Vocabulary only — the rules that read it live on the
 * controller, and the colours themselves in `styles/money.module.scss`, which
 * this imports so the tone → class map cannot drift from the stylesheet.
 */
import styles from '../styles/money.module.scss';

/** Semantic tone of an amount. Maps 1:1 to the classes in `styles/money.module.scss`. */
export const MoneyTones = Object.freeze({
    OWED: 'owed',
    SETTLED: 'settled',
    NEUTRAL: 'neutral',
});

/** Repayment-status tokens, mirroring `resolveRepaymentStatus` in clockout-service. */
export const RepaymentStatuses = Object.freeze({
    OVERDUE: 'overdue',
    PENDING_SETTLEMENT: 'pending_settlement',
    PAID: 'paid',
    NO_ACTIVITY: 'no_activity',
});

/**
 * Tone → the class that paints it, taken from the stylesheet rather than
 * restated here. `money.module.scss` is a CSS Module, so its class names are
 * generated at build time and cannot be written down: every importer gets the
 * same generated name, which is what lets one rule serve the whole app without
 * a global stylesheet.
 */
export const TONE_CLASS_NAMES = Object.freeze({
    [MoneyTones.OWED]: styles.owed,
    [MoneyTones.SETTLED]: styles.settled,
    [MoneyTones.NEUTRAL]: styles.neutral,
});

/**
 * Ledger row type → tone.
 *
 * An advance row is NEUTRAL, not red. Nothing on a ledger row says which
 * advances a settlement paid off — `getUserReport` enriches each row with
 * `rawType`, `type`, `account_number` and `createdAt`, and no per-row settled
 * marker exists — so red would claim a debt the row cannot support, and would
 * paint an advance repaid three cycles ago the same as one taken yesterday.
 * That is the mirror of the bug this replaces, where colouring by the sign of
 * the amount painted every advance green because advances arrive positive.
 *
 * "Owed" is a fact about the member, not about a row, so it stays on the cards,
 * which read `awaitingSettlementAmount` and `overdueAmount`. A row only says
 * which direction the money moved: a settlement came back (green), an advance
 * went out and a fee was charged (both descriptive).
 *
 * An unlisted type still degrades to neutral on the controller.
 */
export const LEDGER_TYPE_TONES = Object.freeze({
    advance: MoneyTones.NEUTRAL,
    settlement: MoneyTones.SETTLED,
    settle: MoneyTones.SETTLED, // rawType, in case a caller passes it through
    service_fee: MoneyTones.NEUTRAL,
    fee: MoneyTones.NEUTRAL, // alias, as in TransactionTypeTag
});

/**
 * The `getUserReport` fields the repayment rules read. Used to tell a real
 * report apart from a failed fetch: `fetchConsumerUser` swallows its error and
 * returns `{}`, and the user page seeds `consumer: {}`, so an empty object is
 * what "the report never arrived" looks like. A payload carrying none of these
 * is not a report of zeroes.
 *
 * A live report always carries `totalAdvances` and `advanceAmount`, including
 * for a member who has never advanced, so this stays true against the service
 * on main as well as against the branch that adds `repaymentStatus`.
 */
export const REPAYMENT_REPORT_FIELDS = Object.freeze([
    'repaymentStatus',
    'isOverdue',
    'isAwaitingSettlement',
    'totalAdvances',
    'advanceAmount',
]);

/** Only USD exists today, so it is the default — but currency stays a parameter. */
export const DEFAULT_CURRENCY = 'USD';

/** Rendered in place of an amount that is missing or unparseable. */
export const DEFAULT_PLACEHOLDER = '—';
